import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '../api/client';

interface UpdateLinkData {
  id: string;
  customTitle?: string | null;
  tags?: string[];
}

export const useUpdateLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, customTitle, tags }: UpdateLinkData) => {
      const response = await customInstance({
        url: `/update-link/${id}`,
        method: 'PUT',
        data: {
          custom_title: customTitle,
          tags,
        },
      });
      return response;
    },
    // Optimistic update: atualiza UI IMEDIATAMENTE antes da API responder
    onMutate: async ({ id, customTitle, tags }) => {
      // 1. Cancela TODAS as queries de links para evitar conflitos
      await queryClient.cancelQueries({ 
        predicate: (query) => 
          Array.isArray(query.queryKey) && 
          (query.queryKey[0] === "/links" || query.queryKey[0] === "/links/search")
      });
      
      // 2. Salva snapshot de TODAS as queries de links para rollback
      const previousQueries = queryClient.getQueriesData({ 
        predicate: (query) => 
          Array.isArray(query.queryKey) && 
          (query.queryKey[0] === "/links" || query.queryKey[0] === "/links/search")
      });
      
      // 3. Função helper para atualizar um link no cache
      const updateLinkInCache = (old: any) => {
        if (!old?.data) return old;
        
        // Cria novo array com link atualizado
        const updatedData = old.data.map((link: any) => {
          if (link.id !== id) return link;
          
          // Atualiza apenas campos fornecidos
          const updated = { ...link };
          if (customTitle !== undefined) {
            updated.customTitle = customTitle;
          }
          if (tags !== undefined) {
            updated.tags = tags;
          }
          
          return updated;
        });
        
        // Retorna novo objeto para forçar re-render
        return {
          ...old,
          data: updatedData,
        };
      };
      
      // 4. Atualiza TODAS as queries de links (lista e search) com qualquer parâmetro
      queryClient.setQueriesData(
        { 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0] === "/links" || query.queryKey[0] === "/links/search")
        },
        updateLinkInCache
      );
      
      // 5. Retorna context para rollback
      return { previousQueries };
    },
    onError: (_error, _variables, context: any) => {
      // Rollback: restaura TODOS os snapshots anteriores
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]: any) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalida todas as queries de links para refetch e garantir sincronização
      queryClient.invalidateQueries({ 
        predicate: (query) => 
          Array.isArray(query.queryKey) && 
          (query.queryKey[0] === "/links" || query.queryKey[0] === "/links/search")
      });
    },
  });
};

